/** @file Emits one line of ASCII text as glyph quads on the font page, 1:1 pixels from the top-left. */
import type { Sink } from './createSink';
import { emitMesh, type PixelMesh } from './emitMesh';

const QUAD = [0, 1, 2, 0, 2, 3];
const QUESTION = 63;
const SPACE = 32;
const xy = new Float64Array(8);
const uv = new Float64Array(8);
/** The glyph primitive, refilled per glyph (no object or array per glyph). */
const mesh: PixelMesh = { page: 0, count: 4, xy, uv, light: 0, lights: null, indices: QUAD };

/** Corners of the glyph quad at (gx, y), w x h pixels, and its page UVs [u0, v0, u1, v1]. */
function glyphQuad(gx: number, y: number, w: number, h: number, g: readonly [number, number, number, number]): void {
  xy[0] = gx;
  xy[1] = y;
  xy[2] = gx + w;
  xy[3] = y;
  xy[4] = gx + w;
  xy[5] = y + h;
  xy[6] = gx;
  xy[7] = y + h;
  const u0 = g[0], v0 = g[1], u1 = g[2], v1 = g[3];
  uv[0] = u0;
  uv[1] = v0;
  uv[2] = u1;
  uv[3] = v0;
  uv[4] = u1;
  uv[5] = v1;
  uv[6] = u0;
  uv[7] = v1;
}

/** Draws `text` with its top-left at (x, y) (round them for crisp glyphs); returns the advance in pixels. */
export function emitText(sink: Sink, text: string, x: number, y: number, light: number): number {
  const { glyphW: w, glyphH: h, glyphs } = sink.atlas;
  mesh.page = sink.font;
  mesh.light = light;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    const g = glyphs.get(code) ?? glyphs.get(QUESTION);
    if (code === SPACE || g === undefined) continue;
    glyphQuad(x + i * w, y, w, h, g);
    emitMesh(sink, mesh);
  }
  return text.length * w;
}
